import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useParams } from "react-router-dom";
import {
    getEventImagePresignedUrls,
    uploadImageToS3,
    createEvent,
    updateEvent,
    getEventById,
    getUsers,
    getPrices,
    assignPhotographers,
    purgeTestEvent,
    getCampaignTimeline,
    getEventOperators,
    lookupOperator,
    setEventOperators,
} from "../../Services/arndAPI";
import Loader from "../../Components/Loader/Loader";
import ConfirmModal from "../../Components/ConfirmModal/ConfirmModal";
import PageHeader from "../../Components/PageHeader/PageHeader";
import Page from "../../Components/Page/Page";
import EventSettings, { validateEventTiming } from "./EventSettings";
import { eventToForm, buildTimingPayload, parseAllowedEmails, uploadEventImages } from "./eventForm";
import { DEFAULT_TIMEZONE, shiftYmd } from "../../Utils/eventTime";
import { fmtCT } from "./campaign/time";
import {
    normalizeEmail,
    addOperator,
    removeOperator,
    hasOperatorEmail,
    operatorLabel,
    operatorEmails,
} from "./operatorField";
import styles from "./Styles/Event.module.css";

const LAST_TIMEZONE_KEY = "lastEventTimezone";

export default function SingleEvent() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isCreate = id === "create";

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("");
    const [photographers, setPhotographers] = useState([]);
    const [selectedPhotographer, setSelectedPhotographer] = useState("");
    // Delete is offered only for events whose *stored* type is "test"
    // (SCRUM-393), judged from the row the server returned — not from the
    // type field being edited, so typing "test" into a real event's form
    // doesn't surface the control. The backend re-checks the same rule.
    const [isTestEvent, setIsTestEvent] = useState(false);
    // SCRUM-438: an event's link is set once — it may already be printed.
    const [slugLocked, setSlugLocked] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    // SCRUM-453: event operators, added by account email. The list is PUT
    // with the event only when it was loaded (edit) and the admin changed it
    // — a failed load must never wipe the live rows.
    const [operators, setOperators] = useState([]);
    const [operatorsState, setOperatorsState] = useState(isCreate ? "ready" : "loading");
    const [operatorsLoadError, setOperatorsLoadError] = useState("");
    const [operatorsTouched, setOperatorsTouched] = useState(false);
    const [operatorInput, setOperatorInput] = useState("");
    const [operatorError, setOperatorError] = useState("");
    const [addingOperator, setAddingOperator] = useState(false);
    // Create mode: the new event's id once created, so a retry after a failed
    // operators save updates that event instead of creating a second one.
    const [createdId, setCreatedId] = useState(null);
    // The route's current event id, so a load for an event the admin has
    // since left can't fill this form with that event's operators.
    const currentIdRef = useRef(id);
    currentIdRef.current = id;

    const [form, setForm] = useState({
        name: "",
        location: "",
        eventType: "",
        iconFile: null,
        bannerFile: null,
        iconUrl: "",
        eventBanner: "",
        // SCRUM-380: comma-separated account emails; blank = public.
        allowedEmails: "",
        // SCRUM-438: the public link arnd.io/app/<slug>; blank = none.
        slug: "",
        // SCRUM-449: automatic campaign sends; off by default.
        autoCampaigns: false,
    });
    // SCRUM-449: the live upcoming steps shown under the toggle.
    const [schedule, setSchedule] = useState(null);
    // Test events never send (the API refuses the toggle), so it is not offered.
    const isTestType = form.eventType.trim().toLowerCase() === "test";
    const showSchedule = !isCreate && !isTestType && form.autoCampaigns;

    // SCRUM-334: timing/prices/matching, edited via the EventSettings section.
    const [settings, setSettings] = useState({
        timezone: (() => {
            try {
                return localStorage.getItem(LAST_TIMEZONE_KEY) || DEFAULT_TIMEZONE;
            } catch {
                return DEFAULT_TIMEZONE;
            }
        })(),
        registrationYmd: "",
        registrationHm: "",
        startYmd: "",
        startHm: "",
        endYmd: "",
        endHm: "",
        expiresYmd: "",
        expiresHm: "",
        imagePrice: "",
        albumPrice: "",
        discountPercent: "",
        matchThreshold: "",
        salesWindowDays: "",
        // Create mode: nothing saved yet, so the auto-fills may run freely.
        expiresTouched: false,
        registrationTouched: false,
    });

    // Fetch event + photographers when editing; pre-fill from Defaults on create.
    useEffect(() => {
        if (isCreate) {
            fetchDefaultsForCreate();
        } else {
            fetchEvent();
            fetchPhotographers();
            fetchOperators();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchDefaultsForCreate = async () => {
        try {
            const d = await getPrices();
            setSettings((prev) => ({
                ...prev,
                imagePrice: d.imagesPrice ?? "",
                albumPrice: d.albumPrice ?? "",
                discountPercent: d.discountAmount ?? "",
                matchThreshold: d.matchThreshold ?? "",
                salesWindowDays: d.expirationTime ?? "",
            }));
        } catch (err) {
            console.error(err);
            setStatus("Could not load Defaults for pre-fill");
        }
    };

    const fetchEvent = async () => {
        setLoading(true);
        try {
            // Defaults are needed on edit too, now — Sales close's "Reset to
            // default" needs salesWindowDays, same as the create path. A
            // Defaults load failure shouldn't block opening the event, so it
            // falls back to {} (Reset to default is then a no-op until retried).
            const [{ eventData }, defaults] = await Promise.all([
                getEventById(id),
                getPrices().catch(() => ({})),
            ]);
            setIsTestEvent((eventData.eventType ?? "").trim().toLowerCase() === "test");
            setSlugLocked(!!eventData.slug);
            setForm({
                name: eventData.name,
                location: eventData.location || "",
                eventType: eventData.eventType || "",
                allowedEmails: (eventData.allowedEmails ?? []).join(", "),
                slug: eventData.slug || "",
                autoCampaigns: !!eventData.autoCampaigns,
                iconFile: null,
                bannerFile: null,
                iconUrl: eventData.iconUrl || "",
                eventBanner: eventData.eventBanner || "",
            });

            const timingForm = eventToForm(
                { ...eventData, timezone: eventData.timezone || DEFAULT_TIMEZONE },
                defaults
            );
            // registrationStartsAt is rolling out on the API in parallel with this
            // form (SCRUM-449 follow-up) — an event fetched before that lands has
            // none yet. Rather than show a blank required field the operator must
            // notice and fill by hand, fall back to the same start-1-day default
            // create mode uses, and leave it un-touched so it keeps tracking the
            // start until the operator (or a later save) pins it down.
            const hasStoredRegistration = !!eventData.registrationStartsAt;
            const registrationFallback =
                !hasStoredRegistration && timingForm.startYmd && timingForm.startHm
                    ? { registrationYmd: shiftYmd(timingForm.startYmd, -1), registrationHm: timingForm.startHm }
                    : {};
            setSettings({
                ...timingForm,
                ...registrationFallback,
                // Edit mode: expiresYmd/expiresHm came from an already-saved event,
                // not a fresh auto-fill — the first end-date edit must not silently
                // overwrite it. registrationTouched mirrors that only when there was
                // a stored value to protect (see fallback above). Lives in state
                // (not a ref inside EventSettings) so it survives this component
                // unmounting on a failed submit (`loading ? <Loader/> : <form>`).
                expiresTouched: true,
                registrationTouched: hasStoredRegistration,
            });

            // pre-select the first assigned photographer if exists
            if (eventData.User && eventData.User.length > 0) {
                setSelectedPhotographer(eventData.User[0].id);
            }
        } catch (err) {
            console.error(err);
            setStatus("Error loading event");
        } finally {
            setLoading(false);
        }
    };

    const fetchOperators = async () => {
        const requestedId = id;
        setOperatorsState("loading");
        try {
            const data = await getEventOperators(requestedId);
            if (currentIdRef.current !== requestedId) return;
            setOperators(data?.operators ?? []);
            setOperatorsTouched(false);
            setOperatorsLoadError("");
            setOperatorsState("ready");
        } catch (err) {
            if (currentIdRef.current !== requestedId) return;
            console.error(err);
            setOperatorsLoadError(err.message || "Error loading operators");
            setOperatorsState("failed");
        }
    };

    const handleAddOperator = async () => {
        const email = normalizeEmail(operatorInput);
        if (!email || addingOperator) return;
        setOperatorError("");
        if (hasOperatorEmail(operators, email)) {
            setOperatorInput("");
            return;
        }
        setAddingOperator(true);
        try {
            const operator = await lookupOperator(email);
            setOperators((list) => addOperator(list, operator));
            setOperatorsTouched(true);
            setOperatorInput("");
        } catch (err) {
            setOperatorError(err.message);
        } finally {
            setAddingOperator(false);
        }
    };

    const handleRemoveOperator = (userId) => {
        setOperators((list) => removeOperator(list, userId));
        setOperatorsTouched(true);
    };

    const fetchPhotographers = async () => {
        try {
            const data = await getUsers({ role: "PHOTOGRAPHER" });
            setPhotographers(data);
        } catch (err) {
            setStatus("Error loading photographers");
        }
    };

    useEffect(() => {
        if (!showSchedule) {
            setSchedule(null);
            return undefined;
        }
        let cancelled = false;
        getCampaignTimeline(id)
            .then((t) => {
                if (cancelled) return;
                const live = (t.upcoming ?? [])
                    .filter((o) => !o.skipped && !o.supersededBy)
                    .sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt));
                setSchedule({ items: live, error: "" });
            })
            .catch((err) => {
                if (!cancelled) setSchedule({ items: [], error: err.message });
            });
        return () => {
            cancelled = true;
        };
    }, [id, showSchedule]);

    const handleSettingsChange = (next) => {
        setSettings(next);
        try {
            localStorage.setItem(LAST_TIMEZONE_KEY, next.timezone);
        } catch {
            /* ignore — remembering the last zone is a convenience, not required */
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const timingError = validateEventTiming(settings);
        if (timingError) {
            setStatus(timingError);
            return;
        }
        if (operatorInput.trim()) {
            setStatus(`Operator "${operatorInput.trim()}" isn't added yet — press Add or clear the box.`);
            return;
        }

        setStatus(isCreate ? "Creating event..." : "Updating event...");
        setLoading(true);

        try {
            const { iconUrl, eventBanner } = await uploadEventImages(form, {
                getPresignedUrls: getEventImagePresignedUrls,
                upload: uploadImageToS3,
                newKey: uuidv4,
            });

            const payload = {
                name: form.name,
                location: form.location,
                eventType: form.eventType,
                iconUrl,
                eventBanner,
                allowedEmails: parseAllowedEmails(form.allowedEmails),
                // Sent only while it can still be set; the API refuses a change.
                ...(!slugLocked && form.slug.trim() ? { slug: form.slug.trim().toLowerCase() } : {}),
                // Only on update: a new event gets its schedule once it exists.
                ...(isCreate ? {} : { autoCampaigns: !isTestType && form.autoCampaigns }),
                ...buildTimingPayload(settings),
            };

            let eventId = id;
            if (isCreate && !createdId) {
                const created = await createEvent(payload);
                eventId = created?.id;
                setCreatedId(eventId);
                setStatus("Event created!");
            } else if (isCreate) {
                eventId = createdId;
                await updateEvent(eventId, payload);
                setStatus("Event created!");
            } else {
                await updateEvent(id, payload);

                // update photographer assignment if selected
                if (selectedPhotographer) {
                    await assignPhotographers(id, [selectedPhotographer]);
                }

                setStatus("Event updated!");
            }

            const saveOperators = isCreate
                ? operatorsTouched && operators.length > 0
                : operatorsState === "ready" && operatorsTouched;
            if (saveOperators) {
                try {
                    if (!eventId) throw new Error("the new event's id was not returned");
                    await setEventOperators(eventId, operatorEmails(operators));
                } catch (err) {
                    console.error(err);
                    setStatus(
                        isCreate
                            ? `Event created, but its operators were not saved: ${err.message}. Press Create Event again to retry — the event won't be created twice.`
                            : `Event saved, but its operators were not saved: ${err.message}`
                    );
                    setLoading(false);
                    return;
                }
            }
            setLoading(false);
            navigate("/events");
        } catch (err) {
            console.error(err);
            setStatus(err.message || "Error saving event");
            setLoading(false);
        }
    };

    const confirmDelete = async () => {
        setConfirmingDelete(false);
        setLoading(true);
        try {
            await purgeTestEvent(id);
            navigate("/events");
        } catch (err) {
            setStatus(err.message || "Error deleting event");
            setLoading(false);
        }
    };

    return (
        <Page narrow>
        <PageHeader title={isCreate ? "Create Event" : "Edit Event"} />
        <div className={styles.container}>
            {loading ? (
                <Loader />
            ) : (
                <form className={styles.formContainer} onSubmit={handleSubmit}>
                    <input
                        type="text"
                        placeholder="Event Name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Location"
                        value={form.location}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                        required
                    />
                    <input
                        type="text"
                        placeholder="Event Type"
                        value={form.eventType}
                        onChange={(e) => setForm({ ...form, eventType: e.target.value })}
                        required
                    />
                    <label className={styles.fieldLabel}>
                        Visible only to (account emails, comma-separated; blank = everyone)
                        <input
                            type="text"
                            placeholder="e.g. imad+reviewer@arnd.io"
                            value={form.allowedEmails}
                            onChange={(e) => setForm({ ...form, allowedEmails: e.target.value })}
                        />
                    </label>
                    <div className={styles.fieldLabel}>
                        <label htmlFor="operator-email">Operators (account emails)</label>
                        {operatorsState === "loading" && <span>Loading operators…</span>}
                        {operatorsState === "failed" && (
                            <span className={styles.fieldError}>
                                Couldn't load operators ({operatorsLoadError}) — they won't be changed when you save. Reload to edit them.
                            </span>
                        )}
                        {operatorsState === "ready" && (
                            <>
                                {operators.length > 0 && (
                                    <div className={styles.chipRow}>
                                        {operators.map((o) => (
                                            <span key={o.userId} className={styles.chip}>
                                                {operatorLabel(o)}
                                                <button
                                                    type="button"
                                                    className={styles.chipRemove}
                                                    aria-label={`Remove ${o.email}`}
                                                    onClick={() => handleRemoveOperator(o.userId)}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className={styles.operatorAdd}>
                                    <input
                                        id="operator-email"
                                        type="text"
                                        placeholder="e.g. mona@example.com"
                                        value={operatorInput}
                                        onChange={(e) => {
                                            setOperatorInput(e.target.value);
                                            setOperatorError("");
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddOperator();
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className={styles.operatorAddBtn}
                                        onClick={handleAddOperator}
                                        disabled={addingOperator || !operatorInput.trim()}
                                    >
                                        {addingOperator ? "Adding…" : "Add"}
                                    </button>
                                </div>
                                {operatorError && <span className={styles.fieldError}>{operatorError}</span>}
                            </>
                        )}
                        <span className={styles.fieldNote}>
                            Operators can open this event's Event Ops (no revenue) and get a copy of its campaign messages.
                        </span>
                    </div>
                    <label className={styles.fieldLabel}>
                        QR link — arnd.io/app/… (set once; blank = none). Scans of it show in this event's funnel.
                        <input
                            type="text"
                            placeholder="e.g. pearland"
                            value={form.slug}
                            disabled={slugLocked}
                            pattern="[a-z0-9][a-z0-9\-]{1,28}[a-z0-9]"
                            title="3–30 characters: a-z, 0-9 and '-', starting and ending with a letter or digit"
                            onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                        />
                        {form.slug.trim() && (
                            <span>
                                https://arnd.io/app/{form.slug.trim()}
                                {slugLocked ? " — locked, it may already be printed" : ""}
                            </span>
                        )}
                    </label>

                    {!isTestType && isCreate && (
                        <div className={styles.autoCampaigns}>
                            <span>Save the event first, then turn on automatic campaigns.</span>
                        </div>
                    )}
                    {!isTestType && !isCreate && (
                        <div className={styles.autoCampaigns}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.autoCampaigns}
                                    onChange={(e) => setForm({ ...form, autoCampaigns: e.target.checked })}
                                />{" "}
                                Automatic campaigns
                            </label>
                            <span>Sends the Campaigns playbook's reminders to this event's parents on schedule.</span>
                            {showSchedule && schedule && (
                                schedule.error ? (
                                    <span>{schedule.error}</span>
                                ) : schedule.items.length === 0 ? (
                                    <span>No automatic steps ahead.</span>
                                ) : (
                                    <ul className={styles.autoSchedule}>
                                        {schedule.items.map((o) => (
                                            <li key={o.id} data-testid="auto-schedule-item">
                                                {fmtCT(o.dueAt)} · {o.name}
                                            </li>
                                        ))}
                                    </ul>
                                )
                            )}
                        </div>
                    )}

                    {/* Icon upload */}
                    <div className={styles.fileInput}>
                        <label>Icon Image:</label>
                        <div className={styles.imagePreview}>
                            {form.iconFile ? (
                                <img src={URL.createObjectURL(form.iconFile)} alt="icon preview" />
                            ) : form.iconUrl ? (
                                <img src={form.iconUrl} alt="current icon" />
                            ) : (
                                <div className={styles.placeholder}>No icon selected</div>
                            )}
                        </div>
                        <input
                            key={form.iconFile ? "icon-picked" : "icon-empty"}
                            type="file"
                            accept="image/*"
                            onChange={(e) => setForm({ ...form, iconFile: e.target.files[0] })}
                            required={isCreate}
                        />
                        {(form.iconFile || form.iconUrl) && (
                            <button
                                type="button"
                                className={styles.removeLink}
                                onClick={() => setForm({ ...form, iconFile: null, iconUrl: "" })}
                            >
                                Remove icon
                            </button>
                        )}
                    </div>

                    {/* Banner upload */}
                    <div className={styles.fileInput}>
                        <label>Banner Image:</label>
                        <div className={styles.imagePreview}>
                            {form.bannerFile ? (
                                <img src={URL.createObjectURL(form.bannerFile)} alt="banner preview" />
                            ) : form.eventBanner ? (
                                <img src={form.eventBanner} alt="current banner" />
                            ) : (
                                <div className={styles.placeholder}>No banner selected</div>
                            )}
                        </div>
                        <input
                            key={form.bannerFile ? "banner-picked" : "banner-empty"}
                            type="file"
                            accept="image/*"
                            onChange={(e) => setForm({ ...form, bannerFile: e.target.files[0] })}
                            required={isCreate}
                        />
                        {(form.bannerFile || form.eventBanner) && (
                            <button
                                type="button"
                                className={styles.removeLink}
                                onClick={() => setForm({ ...form, bannerFile: null, eventBanner: "" })}
                            >
                                Remove banner
                            </button>
                        )}
                    </div>

                    {/* Photographer dropdown (only edit mode) */}
                    {!isCreate && (
                        <div className={styles.fileInput}>
                            <label>Assign Photographer:</label>
                            <select
                                value={selectedPhotographer}
                                onChange={(e) => setSelectedPhotographer(e.target.value)}
                            >
                                <option value="">-- Select Photographer --</option>
                                {photographers.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.fullName || p.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <EventSettings
                        value={settings}
                        onChange={handleSettingsChange}
                    />

                    <button
                        type="submit"
                        className={styles.saveBtn}
                        disabled={!!validateEventTiming(settings)}
                    >
                        {isCreate ? "Create Event" : "Update Event"}
                    </button>
                    {status && <div className={styles.status}>{status}</div>}
                    {!isCreate && isTestEvent && (
                        <button
                            type="button"
                            className={styles.deleteLink}
                            onClick={() => setConfirmingDelete(true)}
                        >
                            Delete this test event
                        </button>
                    )}
                </form>
            )}
            {confirmingDelete && (
                <ConfirmModal
                    message={`Permanently delete the test event "${form.name}"? Every photo, match, face, purchase, RSVP and stored file under it is removed. This cannot be undone.`}
                    onConfirm={confirmDelete}
                    onCancel={() => setConfirmingDelete(false)}
                />
            )}
        </div>
        </Page>
    );
}
